import { coerceCssPixelValue } from '@angular/cdk/coercion';

import {
  Evented,
  SnLonLatBounds,
  SnBounds,
  SnLonLatBoundsLike,
  SnLonLat,
  SnPoint,
} from '@lib/geo/helpers';

import { IGoogleOverlayEvents } from './google-overlay-events';

let uniqueId = 0;

export class GoogleMapImageOverlay<
  TEvents extends IGoogleOverlayEvents = IGoogleOverlayEvents,
> extends Evented<TEvents> {
  public readonly googleMapImageOverlay: google.maps.OverlayView;

  private readonly _url: string;
  private readonly _rotation: number;
  private readonly _lonLatBounds: SnLonLatBounds;
  private readonly _latLng: google.maps.LatLng;
  private readonly _imageSize: google.maps.Size;
  private readonly _markerSize: google.maps.Size;
  private readonly _imageLabel: string;
  private readonly _id: string;
  private _zIndex: number = 1;
  private _opacity: number = 1;
  private _clickable: boolean;

  private readonly _element: HTMLElement;
  private readonly _imageElement: HTMLImageElement;
  private readonly _textBoxElement: HTMLDivElement;

  constructor(data: {
    url: string;
    rotation: number;
    zIndex?: number;
    lonLatBounds?: SnLonLatBoundsLike;
    latLng?: google.maps.LatLng;
    markerSize?: google.maps.Size;
    imageLabel?: string;
    opacity?: number;
    clickable?: boolean;
  }) {
    super();
    this.googleMapImageOverlay = this._createGoogleMapOverlay();
    this._url = data.url;
    this._rotation = data.rotation;
    this._lonLatBounds = SnLonLatBounds.convert(data.lonLatBounds);
    this._latLng = data.latLng;
    this._imageSize = new google.maps.Size(80, 80);
    this._markerSize = data.markerSize;
    this._imageLabel = data.imageLabel;
    this._id = `sn-map-overlay-${uniqueId++}`;
    this._zIndex = data.zIndex;
    this._opacity = data.opacity;
    this._clickable = data.clickable;

    this._element = this._createElement();
    this._imageElement = this._createImageElement();
    if (this._imageLabel) {
      this._textBoxElement = this._createTextBoxElement();
      this._element.appendChild(this._textBoxElement);
    }
  }

  get latLng(): google.maps.LatLng {
    return this._latLng;
  }

  public getId(): string {
    return this._id;
  }

  public getZIndex(): number {
    return this._zIndex;
  }

  public setZIndex(zIndex: number): this {
    if (this._zIndex !== zIndex) {
      this._zIndex = zIndex;
    }

    return this;
  }

  public getOpacity(): number {
    return this._opacity;
  }

  public setOpacity(opacity: number): this {
    if (this._opacity !== opacity) {
      this._opacity = opacity;
    }

    return this;
  }

  public getClickable(): boolean {
    return this._clickable;
  }

  public setClickable(clickable: boolean): this {
    if (this._clickable !== clickable) {
      this._clickable = clickable;
    }

    return this;
  }

  public draw(): void {
    this._updatePosition();
    this._updateSize();
  }

  public onAdd(): void {
    this.googleMapImageOverlay.getPanes().overlayLayer.appendChild(this._element);
  }

  public onRemove(): void {
    this._element.parentElement.removeChild(this._element);
  }

  private _createElement(): HTMLElement {
    const element = document.createElement('div');
    element.style.position = 'absolute';
    element.style.transform = `rotate(${this._rotation}deg)`;

    return element;
  }

  private _createImageElement(): HTMLImageElement {
    const image = document.createElement('img');
    image.src = this._url;
    image.crossOrigin = 'Anonymous';
    this._element.appendChild(image);

    return image;
  }

  private _createTextBoxElement(): HTMLDivElement {
    const element = document.createElement('div');
    const text = document.createElement('div');
    text.innerHTML = this._imageLabel;
    element.appendChild(text);
    element.style.position = 'absolute';
    element.style.width = coerceCssPixelValue(this._imageSize.width - 10);
    element.style.right = coerceCssPixelValue(-(this._imageSize.width - 10));
    element.style.height = '100%';
    element.classList.add('fs__image-overlay-textbox');
    text.classList.add('fs-text-truncate');

    return element;
  }

  private _updatePosition(): void {
    const element = this._element;
    const topLeft = this._latLng
      ? this._fromLonLatToPoint(new SnLonLat(this._latLng.lng(), this._latLng.lat()))
      : this._fromLonLatToPoint(this._lonLatBounds.getNorthWest());
    element.style.left = coerceCssPixelValue(topLeft.x);
    element.style.top = coerceCssPixelValue(topLeft.y);
    if (this._latLng) {
      element.style.width = coerceCssPixelValue(this._imageSize.width);
      element.style.height = coerceCssPixelValue(this._imageSize.height);
      element.style.marginLeft = coerceCssPixelValue(-(this._imageSize.width / 2));
      element.style.marginTop = coerceCssPixelValue(
        -(this._imageSize.height + 20 + this._markerSize.height),
      );
      element.style.borderRadius = this._imageLabel ? '50% 0 0 50%' : '50%';
      element.classList.add('fs__pano-image-overlay');
    }
  }

  private _updateSize(): void {
    const image = this._imageElement;
    if (this._lonLatBounds) {
      const topLeft = this._fromLonLatToPoint(this._lonLatBounds.getNorthWest());
      const bottomRight = this._fromLonLatToPoint(this._lonLatBounds.getSouthEast());
      const bounds = new SnBounds(topLeft, bottomRight);

      const size = bounds.getSize();
      image.style.width = coerceCssPixelValue(size.x);
      image.style.height = coerceCssPixelValue(size.y);
    } else {
      image.style.width = coerceCssPixelValue(this._imageSize.width - 10);
      image.style.height = coerceCssPixelValue(this._imageSize.height - 10);
    }
  }

  private _fromLonLatToPoint(lonLat: SnLonLat): SnPoint {
    const projection = this.googleMapImageOverlay.getProjection();
    const googleLatLng = this._toGoogleMapLatLng(lonLat);
    const googlePoint = projection.fromLatLngToDivPixel(googleLatLng);

    return this._fromGoogleMapPoint(googlePoint);
  }

  private _fromGoogleMapPoint(point: google.maps.Point): SnPoint {
    return new SnPoint(point.x, point.y);
  }

  private _toGoogleMapLatLng(lonLat: SnLonLat): google.maps.LatLng {
    return new google.maps.LatLng(lonLat?.lat, lonLat?.lon);
  }

  private _createGoogleMapOverlay(): google.maps.OverlayView {
    const googleOverlay = new google.maps.OverlayView();
    googleOverlay.onAdd = () => this.onAdd();
    googleOverlay.draw = () => this.draw();
    googleOverlay.onRemove = () => this.onRemove();

    return googleOverlay;
  }
}
